/**
 * School license key handling (CL-131).
 *
 * Schools receive a link like https://earthvoice-v2.pages.dev/?license=ev-<24 hex>.
 * On startup, initLicense() persists the key to localStorage ('ev:license')
 * and strips the param from the URL so it doesn't linger in the address bar,
 * bookmarks, or screenshots. Every API call then sends the key as an
 * X-EV-License header, which the Worker uses for per-school rate limits and
 * usage attribution. No student accounts, no PII — the key identifies the
 * school, never the student.
 */
import { createLogger } from './logger.js'

const log = createLogger('license')

const STORAGE_KEY = 'ev:license'

/**
 * Read ?license=... from the URL, persist it, and strip it from the address
 * bar. Call once from main.js before the first API request. Safe to call in
 * non-browser contexts (no-op) and when storage/history throw (license is
 * optional — anonymous behavior is unchanged).
 */
export function initLicense() {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    const key = (url.searchParams.get('license') || '').trim()
    if (!key) return
    localStorage.setItem(STORAGE_KEY, key)
    url.searchParams.delete('license')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
    log.info('license key stored from URL param')
  } catch (err) {
    // localStorage/history can throw in sandboxed contexts
    log.warn('license init failed', err)
  }
}

/** The stored license key, or null when running anonymously. */
export function getLicenseKey() {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || null
  } catch {
    return null
  }
}

/**
 * Headers to spread into API fetches: {} when anonymous, or
 * { 'X-EV-License': key } when a school license is stored.
 */
export function licenseHeaders() {
  const key = getLicenseKey()
  return key ? { 'X-EV-License': key } : {}
}
